import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import {
  WebApiPurchaseOrder,
  SalesTax,
  LineDataBySupplier,
  ISubcategory,
  IPurchaseOrder,
  IInvoice,
  StockCostResponse,
  IStockCost,
  IItem,
  IBrand,
  IUnit,
  LineDataVariant,
  Invoice,
  PurchaseOrderLine,
  ISupplier,
  IStockReceived,
  ICostDifference,
} from '$models'
import { PurchaseOrderUi } from '../../ordering/purchase-orders/types'
import {
  categoryService,
  invoiceService,
  purchaseOrderService,
  salesTaxService,
  stockCostService,
  stockReceivedService,
  costDifferenceService,
} from '$services'
import {
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
  findMatching,
  roundNumber,
} from '$utils'
import { InvoiceQueryConditions } from '../slice'
import dayjs, { Dayjs } from 'dayjs'
import { UiInvoice } from './helpers/uiInvoice'
import { UiInvoiceLine } from './helpers/uiInvoiceLine'
import {
  calculateTaxRelatedFields,
  combineWithPurchaseOrderLines,
  extractLineDataVariants,
  fetchAndUpdateLineCosts,
  findSupplierVariantByCode,
  getDefaultSupplierVariant,
  removePurchaseOrderLines,
  transformApiInvoiceToUiInvoice,
  transformInvoiceLinesToGridInvoiceLines,
  updateInvoiceLinesWithSupplierVariants,
} from './helpers/helper'
import { convertPurchaseOrderToPurchaseOrderUi } from '../../ordering/purchase-orders/helpers/helper'
import useStockStore from 'src/components/routes/stock/store'
import {
  calculateFooterTotals,
  calculateTaxOnItem,
  calculateTotalCost,
} from '../../_common/helpers/helper'
import { API_DATE_FORMAT, MatchedOnType } from '$constants'
import { dictionaryToArray } from '../helper'
import { LoaderFunctionArgs } from 'react-router-dom'
import { InvoiceSearchResult } from '@loadedhub/loaded-stock-api-sdk'
import { InvoiceVersion } from '$constants'

interface InvoiceState {
  initialInvoice: IInvoice | null
  salesTax: SalesTax[]
  roundingAmount: number
  showDiscountedItemCosts: boolean
  purchaseOrders: IPurchaseOrder[]
  subcategories: ISubcategory[]
  linkedPurchaseOrder: IPurchaseOrder | null
  lineDataBySupplier: LineDataBySupplier | null
  isGridLoading: boolean
  isReceiveGoodsButtonEnabled: boolean
  matchingInvoices: IInvoice[]
  itemCosts: {
    [key: string]: IStockCost
  }
  invoice: UiInvoice
  invoiceLinesById: { [key: string]: UiInvoiceLine } // Dictionary of lines keyed by line.id
  priceAlertRecentStockCostHistory: { [key: string]: IStockReceived[] }
  priceAlertDishRecipes: { [key: string]: ICostDifference[] }
  awaitingCost: boolean
  isManualInvoiceEntry: boolean
  isFromPurchaseOrder: boolean
  invoiceSearchResults: InvoiceSearchResult[]
  isLoadingInvoiceSearch: boolean
  processingInvoiceId: string | null
  processedInvoice: UiInvoice | null
  isUploading: boolean
}

export interface InvoiceSlice extends InvoiceState {
  resetInvoiceSlice: () => void

  /**
   * Recalculates and updates invoice subtotal and tax amount based on current lines
   * This is called internally whenever invoice lines or related fields change
   */
  recalculateInvoiceTotals: () => void

  /**
   * Set the uploading state
   * @param isUploading - Whether an invoice file is currently being uploaded
   */
  setIsUploading: (isUploading: boolean) => void

  /**
   * Set manual invoice entry mode
   * @param isManualInvoiceEntry - Whether the user is manually entering invoice details
   */
  setIsManualInvoiceEntry: (isManualInvoiceEntry: boolean) => void

  /**
   * Set from purchase order flag
   * @param isFromPurchaseOrder - Whether the invoice was created from a purchase order
   */
  setIsFromPurchaseOrder: (isFromPurchaseOrder: boolean) => void

  /**
   * Start polling for invoice processing status
   * @param invoiceId - The ID of the invoice to poll for processing status
   * @param onComplete - Optional callback when processing is complete, receives the linked invoice
   * @param onTimeout - Optional callback when polling times out
   */
  startInvoiceProcessingPolling: (
    invoiceId: string,
    onComplete?: (invoice: UiInvoice | null) => void,
    onTimeout?: () => void
  ) => void

  /**
   * Stop invoice processing polling
   */
  stopInvoiceProcessingPolling: () => void

  /**
   * Get an invoice
   * @param id the id of the invoice
   * @param suppressErrorTypes optional array of HTTP status codes to suppress error messages for
   */
  loadInvoice: (
    id: string,
    suppressErrorTypes?: number[]
  ) => Promise<IInvoice | null>

  /**
   * Get an initial invoice
   * @param id the id of the invoice
   */
  getInitialInvoice: (id: string) => Promise<void>

  /**
   * Get sales tax
   */
  getSalesTax: () => Promise<SalesTax[]>

  /**
   * Get a purchase order
   * @param query search query for puchase orders, can be order number or supplier name
   */
  getPurchaseOrders: (query: string) => Promise<void>

  /**
   * GetWebAPI purchase order
   */
  getWebApiPurchaseOrder: (id: string) => Promise<WebApiPurchaseOrder | null>

  /**
   * Get a purchase order
   */
  getPurchaseOrder: (id: string) => Promise<IPurchaseOrder | null>

  /**
   * Get a purchase order
   */
  getLinkedPurchaseOrder: (id?: string) => Promise<IPurchaseOrder | null>

  /**
   * Gets all subcategories
   */
  getSubcategories: () => Promise<void>

  /**
   * Gets the latest cost for all items as at the given date
   */
  getLatestItemCosts: (
    receivedDate: Dayjs,
    ids: string[]
  ) => Promise<{ [key: string]: IStockCost } | null>

  /**
   * Update a purchase order
   */
  updatePurchaseOrderWebApi: (
    purchaseOrder: WebApiPurchaseOrder
  ) => Promise<void>

  /**
   * Update an invoice
   * @param id the id of the invoice
   */
  updateInvoice: (
    invoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IInvoice | null>

  /**
   * Add an invoice
   * @param invoice the invoice to be added
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  addInvoice: (
    invoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<IInvoice | null>

  /**
   * Remove an invoice
   * @param id the id of the invoice
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  removeInvoice: (
    id: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>

  initializeInvoiceData: (loaderFunctionArgs: LoaderFunctionArgs) => Promise<{
    invoice: IInvoice | null
    suppliers: Record<string, ISupplier> | null
    items: Record<string, IItem> | null
    brands: Record<string, IBrand> | null
    units: Record<string, IUnit> | null
    salesTax: SalesTax[]
    linkedPurchaseOrder: IPurchaseOrder | null
  }>

  /**
   * Create a new invoice from a purchase order
   * @param purchaseOrderUi the purchase order UI data to create the invoice from
   */
  createInvoiceFromPurchaseOrder: (
    purchaseOrderUi: PurchaseOrderUi
  ) => Promise<{ invoice: IInvoice; purchaseOrder: IPurchaseOrder } | null>

  setLinkedPurchaseOrder: (purchaseOrder: IPurchaseOrder | undefined) => void

  setLineDataBySupplier: (lineDataBySupplier: LineDataBySupplier) => void

  setIsGridLoading: (bool: boolean) => void

  setInvoice: (invoice: Partial<UiInvoice>) => void

  setLines: (lines: { [key: string]: UiInvoiceLine }) => void

  updateInvoiceLine: (
    lineId: string,
    updatedFields: Partial<UiInvoiceLine>
  ) => UiInvoiceLine

  updateInvoiceLineItem: (invoiceLineId: string, updatedItem: IItem) => void

  mergeLineIfApplicable: (invoiceLine: UiInvoiceLine) => void

  clearInvoiceLineCode: (invoiceLineId: string) => void

  updateInvoiceLineCode: (invoiceLineId: string, updatedCode: string) => void

  updateInvoiceLineBrand: (invoiceLineId: string, updatedBrand: IBrand) => void

  updateInvoiceLineUnit: (invoiceLineId: string, updatedUnit: IUnit) => void

  updateInvoiceLineUnitCost: (
    invoiceLineId: string,
    updatedUnitCost: number
  ) => void

  updateInvoiceLineQtdReceived: (
    invoiceLineId: string,
    updatedQuantityReceived: number
  ) => void

  updateInvoiceLineSelectedSalesTax: (
    invoiceLineId: string,
    updatedSelectedSalesTax: number
  ) => void

  updateInvoiceLineCustomSalesTax: (
    invoiceLineId: string,
    updatedCustomSalesTax: number
  ) => void

  updateInvoiceLineTotalCost: (
    invoiceLineId: string,
    updatedTotalCost: number
  ) => void

  updateInvoiceLineDeletedAt: (
    invoiceLineId: string,
    updatedDeletedAt: dayjs.Dayjs | undefined
  ) => void

  updateInvoiceLineTaxRate: (
    invoiceLineId: string,
    updatedTaxRate: number
  ) => void

  updateLineWithVariant: (line: UiInvoiceLine, variant: LineDataVariant) => void

  updateLineWithItem: (line: UiInvoiceLine, item: IItem) => void

  updateInvoiceSupplier: (
    supplierName: string | undefined,
    supplierId?: string
  ) => void

  updateInvoiceReceivedAt: (updatedReceivedAt: dayjs.Dayjs | undefined) => void

  updateInvoiceReferenceNumber: (
    updatedReferenceNumber: string | undefined
  ) => void

  updateInvoiceIssuedAt: (updatedIssuedAt: string | undefined) => void

  updateInvoiceDueAt: (updatedDueAt: string | undefined) => void

  updateInvoiceTotal: (updatedTotal: number | undefined) => void

  updateInvoiceDiscount: (updatedDiscount: number | undefined) => void

  updateDiscountPercentage: () => void

  updateInvoiceUnitCostIncludesTax: (updateUnitCostIncludesTax: boolean) => void

  updateInvoiceLineLinks: (supplierId: string | undefined) => void

  mergePurchaseOrderLinesWithInvoiceLines: (
    purchaseOrder: IPurchaseOrder | undefined,
    isFromPurchaseOrder?: boolean
  ) => void

  addInvoiceLine: (lineId: string, newLine: UiInvoiceLine) => void

  deleteInvoiceLine: (lineId: string) => void

  setIsReceiveGoodsButtonEnabled: (bool: boolean) => void

  setShowDiscountedItemCosts: (bool: boolean) => void

  setAwaitingCost: (bool: boolean) => void

  queryForMatchingInvoice: (
    invoiceNumber: string,
    supplierName: string
  ) => Promise<IInvoice[] | null>

  populatePriceAlertRecentStockHistory: (
    itemId: string,
    invoiceReceivedAt: Dayjs
  ) => Promise<IStockReceived[] | undefined>

  populatePriceAlertDishRecipes: (
    itemId: string,
    invoiceId: string
  ) => Promise<ICostDifference[] | undefined>

  /**
   * Retrieves the stock costs of the given items, recipes or all items in a given stocktake
   * @param itemIdTimeStrings An Array of comma seperated strings of the form {itemID},{timeItemShouldBeCostedAt}
   * @param recipeIdTimeStrings An Array of comma seperated strings of the form {recipeID},{timeRecipeShouldBeCostedAt}
   * @param stocktakeId A stocktake ID
   * @returns StockCostResponse holding the costs of all items and recipes passed in the request
   */
  getStockCost: (
    itemIdTimeStrings?: string[],
    recipeIdTimeStrings?: string[],
    variantIdTimeStrings?: string[],
    stocktakeId?: string
  ) => Promise<StockCostResponse | null>

  /**
   * Search for invoices by term
   * @param searchTerm - The search term to match against invoice numbers and supplier names
   * @param limit - Maximum number of results to return (optional, defaults to 20)
   * @returns an array of matching invoices
   */
  searchInvoices: (
    searchTerm: string,
    limit?: number,
    unreceivedWithoutPo?: boolean
  ) => Promise<InvoiceSearchResult[] | null>

  /**
   * Link a invoice to a purchase order
   * @param invoiceId - The ID of the invoice that will be linked
   * @returns The linked invoice with its data
   */
  linkInvoiceToPurchaseOrder: (invoiceId?: string) => Promise<UiInvoice | null>

  /**
   * Unlink an invoice from a purchase order
   * Clears invoice-specific fields and resets lines to purchase order only
   */
  unlinkInvoiceFromPurchaseOrder: () => void

  /**
   * Link the processed invoice to the purchase order
   * Note: File viewer should be opened in component context via onComplete callback
   */
  linkProcessedInvoice: () => Promise<void>

  /**
   * Clear processing state
   */
  clearProcessingState: () => void
}

const initialState: InvoiceState = {
  initialInvoice: null,
  roundingAmount: 0,
  showDiscountedItemCosts: false,
  salesTax: [],
  purchaseOrders: [],
  subcategories: [],
  linkedPurchaseOrder: null,
  lineDataBySupplier: null,
  isGridLoading: false,
  isReceiveGoodsButtonEnabled: false,
  awaitingCost: false,
  matchingInvoices: [],
  itemCosts: {},
  invoice: {} as UiInvoice,
  invoiceLinesById: {},
  priceAlertRecentStockCostHistory: {},
  priceAlertDishRecipes: {},
  isManualInvoiceEntry: false,
  isFromPurchaseOrder: false,
  invoiceSearchResults: [],
  isLoadingInvoiceSearch: false,
  processingInvoiceId: null,
  processedInvoice: null,
  isUploading: false,
}

// Module-level polling state
let invoiceProcessingPollTimeoutId: NodeJS.Timeout | null = null
let invoiceProcessingPollCount = 0
const MAX_INVOICE_POLL_COUNT = 12 // 12 * 10 seconds = 120 seconds max polling
const INVOICE_POLL_INTERVAL = 10000 // 10 seconds
const INITIAL_POLL_DELAY = 15000 // 15 seconds initial delay

const createInvoiceSlice: StateCreator<
  InvoiceSlice & SharedSlice,
  [],
  [],
  InvoiceSlice
> = (set, get) => ({
  ...initialState,

  resetInvoiceSlice: () => {
    set(initialState)
  },

  recalculateInvoiceTotals: () => {
    set((state) => {
      const invoiceLines = dictionaryToArray(state.invoiceLinesById)
      const { subtotal, taxTotal } = calculateFooterTotals(
        invoiceLines,
        state.invoice?.unitCostIncludesTax ?? false,
        state.invoice?.total,
        state.invoice.discountAmount
      )

      return {
        ...state,
        invoice: {
          ...state.invoice,
          subtotal,
          taxAmount: taxTotal,
        },
      }
    })
  },

  setIsManualInvoiceEntry: (isManualInvoiceEntry: boolean): void => {
    set(() => ({ isManualInvoiceEntry }))
  },

  setIsFromPurchaseOrder: (isFromPurchaseOrder: boolean): void => {
    set(() => ({ isFromPurchaseOrder }))
  },

  setIsUploading: (isUploading: boolean): void => {
    set(() => ({ isUploading }))
  },

  startInvoiceProcessingPolling: (invoiceId, onComplete, onTimeout) => {
    // Reset polling state - clear any existing timeout and reset counter
    if (invoiceProcessingPollTimeoutId)
      clearTimeout(invoiceProcessingPollTimeoutId)
    invoiceProcessingPollCount = 0

    // Set processing state
    set(() => ({
      processingInvoiceId: invoiceId,
      processedInvoice: null,
    }))

    // Recursive polling function that calls itself
    const poll = async () => {
      try {
        // Check if we've exceeded max poll count (timeout)
        if (invoiceProcessingPollCount >= MAX_INVOICE_POLL_COUNT) {
          get().stopInvoiceProcessingPolling()

          // Clear processing state on timeout
          set((state) => ({
            ...state,
            processingInvoiceId: null,
            processedInvoice: null,
          }))

          if (onTimeout) {
            onTimeout()
          }
          return
        }

        // Load the invoice to check its status
        // We suppress 404 errors here as the invoice may not be immediately available
        const invoice = await get().loadInvoice(invoiceId, [404])

        // If invoice is processed (Current version), stop polling and complete
        if (invoice?.version === InvoiceVersion.Current) {
          get().stopInvoiceProcessingPolling()

          try {
            const { linkedPurchaseOrder } = get()
            const { suppliers } = useStockStore.getState()

            // Transform the invoice to UI format
            const transformedInvoice = transformApiInvoiceToUiInvoice(
              invoice,
              linkedPurchaseOrder,
              suppliers
            )

            // Store the processed invoice
            set(() => ({
              processingInvoiceId: null,
              processedInvoice: transformedInvoice,
            }))

            // Link the processed invoice to the purchase order
            await get().linkProcessedInvoice()

            // Call the completion callback - component will handle opening file viewer
            if (onComplete) {
              onComplete(transformedInvoice)
            }
          } catch (error) {
            exceptionMessageHandler(error, 'Failed to process invoice')
          }
          return
        }

        // Continue polling - increment count and schedule next poll
        invoiceProcessingPollCount += 1
        invoiceProcessingPollTimeoutId = setTimeout(poll, INVOICE_POLL_INTERVAL)
      } catch (error) {
        // Stop polling on error first
        get().stopInvoiceProcessingPolling()

        exceptionMessageHandler(
          error,
          'Failed to check invoice processing status'
        )
      }
    }

    // Start the initial poll after the initial delay (30 seconds)
    invoiceProcessingPollTimeoutId = setTimeout(poll, INITIAL_POLL_DELAY)
  },

  stopInvoiceProcessingPolling: () => {
    // Clean shutdown of polling mechanism
    if (invoiceProcessingPollTimeoutId)
      clearTimeout(invoiceProcessingPollTimeoutId)
    invoiceProcessingPollTimeoutId = null
    invoiceProcessingPollCount = 0
  },

  loadInvoice: async (
    id: string,
    suppressErrorTypes?: number[]
  ): Promise<IInvoice | null> => {
    try {
      const invoice = await invoiceService.getInvoice(id)
      return invoice
    } catch (error) {
      const errorStatus =
        error && typeof error === 'object' && 'status' in error
          ? error.status
          : null
      const shouldSuppress =
        suppressErrorTypes &&
        typeof errorStatus === 'number' &&
        suppressErrorTypes.includes(errorStatus)

      if (!shouldSuppress) {
        exceptionMessageHandler(error, 'Failed to retrieve invoice')
      }
      return null
    }
  },

  getInitialInvoice: async (id: string): Promise<void> => {
    try {
      const initialInvoice = await invoiceService.getInitialInvoice(id)
      set(() => ({
        initialInvoice,
      }))
    } catch (error) {
      set(() => ({
        initialInvoice: undefined,
      }))
      exceptionMessageHandler(error, 'Failed to retrieve the initial invoice')
    }
  },

  getSalesTax: async (): Promise<SalesTax[]> => {
    try {
      const salesTax = (await salesTaxService.getAllSalesTaxRates()).sort(
        (a, b) => a.sortOrder - b.sortOrder
      )
      set(() => ({
        salesTax,
      }))
      return salesTax
    } catch (error) {
      set(() => ({
        salesTax: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve sales Tax')
      return []
    }
  },

  getPurchaseOrders: async (query: string): Promise<void> => {
    return purchaseOrderService
      .getPurchaseOrders(query)
      .then((purchaseOrders) => {
        // we add here the purchase orders that not exists in the state yet
        set((state) => {
          const updatedPurchaseOrders = [...state.purchaseOrders]

          // Iterate through servicePurchaseOrders and add any missing items
          purchaseOrders.forEach((purchaseOrder) => {
            const isAlreadyInState = state.purchaseOrders.some(
              (order) => order.id === purchaseOrder.id
            )

            if (!isAlreadyInState) {
              updatedPurchaseOrders.push(purchaseOrder)
            }
          })

          return {
            purchaseOrders: updatedPurchaseOrders,
          }
        })
      })
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to retrieve purchase orders')
        set(() => ({
          purchaseOrders: [],
        }))
      })
  },

  getWebApiPurchaseOrder: async (
    id: string
  ): Promise<WebApiPurchaseOrder | null> => {
    set(() => ({ isLoading: true }))
    try {
      return await purchaseOrderService.getWebApiPurchaseOrder(id)
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getPurchaseOrder: async (id: string): Promise<IPurchaseOrder | null> => {
    set(() => ({ isLoading: true }))
    try {
      const purchaseOrder = await purchaseOrderService.getPurchaseOrder(id)
      const statePurchaseOrders = get().purchaseOrders
      if (
        purchaseOrder &&
        !statePurchaseOrders.some(
          (statePurchaseOrder) => statePurchaseOrder.id === purchaseOrder.id
        )
      ) {
        statePurchaseOrders.push(purchaseOrder)
        set(() => ({
          purchaseOrders: [...statePurchaseOrders],
        }))
      }
      return purchaseOrder
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getLinkedPurchaseOrder: async (
    id?: string
  ): Promise<IPurchaseOrder | null> => {
    try {
      set(() => ({ isLoading: true }))
      const linkedPurchaseOrder = id ? await get().getPurchaseOrder(id) : null

      set(() => ({
        linkedPurchaseOrder,
      }))

      return linkedPurchaseOrder
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getSubcategories: async (): Promise<void> => {
    try {
      const subcategories = await categoryService.getAllSubcategories()
      set(() => ({
        subcategories,
      }))
    } catch (error) {
      set(() => ({
        subcategories: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve subcategories')
    }
  },

  getLatestItemCosts: async (
    receivedDate: Dayjs,
    ids: string[]
  ): Promise<{ [key: string]: IStockCost } | null> => {
    try {
      const itemTimeStrings = ids.map((id) => `${id},${receivedDate.format()}`)

      const response = await stockCostService.getStockCost(itemTimeStrings)
      const itemCosts = response?.itemCosts ?? {}

      const newItemCosts = Object.entries(itemCosts).reduce(
        (acc, [itemId, costs]) => ({
          ...acc,
          [itemId]: costs[0],
        }),
        {}
      )

      set((state) => ({
        ...state,
        itemCosts: {
          ...state.itemCosts,
          ...newItemCosts,
        },
      }))

      return newItemCosts
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve previous item costs')
      return null
    }
  },

  updatePurchaseOrderWebApi: async (
    purchaseOrder: WebApiPurchaseOrder
  ): Promise<void> => {
    set(() => ({ isLoading: true }))
    try {
      await purchaseOrderService.updatePurchaseOrderWebApi(purchaseOrder)
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update a purchase order')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateInvoice: async (
    invoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ): Promise<IInvoice | null> => {
    set(() => ({ isLoading: true }))

    return invoiceService
      .updateInvoice(invoice)
      .then((invoice) => {
        set(() => ({ isLoading: false }))
        enqueueLoadedSnackbar(successMessage, 'success')
        return invoice
      })
      .catch((error) => {
        set(() => ({ isLoading: false }))
        exceptionMessageHandler(
          error,
          errorMessage ?? 'Failed to update invoice'
        )
        return null
      })
  },

  addInvoice: async (
    invoice: IInvoice,
    successMessage?: string,
    errorMessage?: string
  ): Promise<IInvoice | null> => {
    set(() => ({ isLoading: true }))

    return invoiceService
      .addInvoice(invoice)
      .then((invoice) => {
        enqueueLoadedSnackbar(successMessage, 'success')
        set(() => ({ isLoading: false }))
        return invoice
      })
      .catch((error) => {
        set(() => ({ isLoading: false }))
        exceptionMessageHandler(error, errorMessage ?? 'Failed to add invoice')
        return null
      })
  },

  removeInvoice: async (
    id: string,
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    // attempt to remove the invoice via the API
    // Note we dont:
    // - update the local state to remove the invoice, so that if the API delete
    //   fails we haven't lost the information about the invoice
    // - set isLoading back to false once we've completed the call successfully,
    //   this is because we expect the caller to have navigated to a page that
    //   will do it's own loading and we don't want a flash of one loading
    //   finishing and another one starting
    let ok = true
    await invoiceService
      .removeInvoice(id)
      .then(() => enqueueLoadedSnackbar(successMessage, 'success'))
      .catch((error) => {
        ok = false
        exceptionMessageHandler(
          error,
          errorMessage ?? 'Failed to delete invoice'
        )
      })

    return ok
  },

  initializeInvoiceData: async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<{
    invoice: IInvoice | null
    suppliers: Record<string, ISupplier> | null
    items: Record<string, IItem> | null
    brands: Record<string, IBrand> | null
    units: Record<string, IUnit> | null
    salesTax: SalesTax[]
    linkedPurchaseOrder: IPurchaseOrder | null
  }> => {
    try {
      const {
        loadInvoice,
        setLines,
        getInitialInvoice,
        getSuppliers,
        getItems,
        getBrands,
        getStockUnits,
        getSalesTax,
        getSubcategories,
        queryForMatchingInvoice,
        getLinkedPurchaseOrder,
        setLineDataBySupplier,
        getLatestItemCosts,
        setInvoice,
        createInvoiceFromPurchaseOrder,
        getPurchaseOrder,
      } = useStockStore.getState()

      let invoiceId = params.invoiceId

      if (!params.invoiceId) {
        // Check if we have a purchase order ID from URL params for creating an invoice
        const url = new URL(request.url)
        const purchaseOrderId = url.searchParams.get('purchaseOrderId')

        if (purchaseOrderId) {
          // Set isFromPurchaseOrder flag when we detect purchase order in URL
          set(() => ({ isFromPurchaseOrder: true }))

          try {
            const purchaseOrder = await getPurchaseOrder(purchaseOrderId)

            if (!purchaseOrder) {
              throw new Error('Failed to load purchase order')
            }

            const purchaseOrderUi =
              convertPurchaseOrderToPurchaseOrderUi(purchaseOrder)

            if (purchaseOrderUi?.linkedInvoiceId) {
              invoiceId = purchaseOrderUi.linkedInvoiceId
            } else {
              // Set isManualInvoiceEntry to true by default for new invoices from purchase orders
              set(() => ({ isManualInvoiceEntry: true }))
              // If PO is already linked to an invoice, we shouldn't create a new one
              // Instead, we should load the existing invoice
              if (!purchaseOrderUi.linkedInvoiceId) {
                const result = await createInvoiceFromPurchaseOrder(
                  purchaseOrderUi
                )

                const [
                  suppliers,
                  items,
                  brands,
                  units,
                  salesTax,
                  _subcategories,
                ] = await Promise.all([
                  getSuppliers(),
                  getItems(),
                  getBrands(),
                  getStockUnits(true),
                  getSalesTax(),
                  getSubcategories(),
                ])

                // Set the initial invoice for new invoices created from purchase orders
                // Without this, the UI won't be able to display the input icons
                if (result?.invoice?.id) {
                  await getInitialInvoice(result.invoice.id)
                }

                return {
                  invoice: result?.invoice ?? null,
                  suppliers,
                  items,
                  brands,
                  units,
                  salesTax,
                  linkedPurchaseOrder: result?.purchaseOrder ?? null,
                }
              }
            }
          } catch (error) {
            exceptionMessageHandler(
              error,
              'Failed to load purchase order from URL parameter'
            )
          }
        }
      }

      const invoicePromise = invoiceId ? loadInvoice(invoiceId) : null
      const initialInvoicePromise = invoiceId
        ? getInitialInvoice(invoiceId)
        : null
      const suppliersPromise = getSuppliers()
      const itemsPromise = getItems()
      const brandsPromise = getBrands()
      const stockUnitsPromise = getStockUnits(true)
      const salesTaxPromise = getSalesTax()
      const subcategoriesPromise = getSubcategories()

      const [
        invoice,
        _initialInvoice,
        suppliers,
        items,
        brands,
        units,
        salesTax,
        _subcategories,
      ] = await Promise.all([
        invoicePromise,
        initialInvoicePromise,
        suppliersPromise,
        itemsPromise,
        brandsPromise,
        stockUnitsPromise,
        salesTaxPromise,
        subcategoriesPromise,
      ])

      if (invoice?.referenceNumber && invoice?.supplierName) {
        await queryForMatchingInvoice(
          invoice.referenceNumber,
          invoice.supplierName
        )
      }

      const linkedPurchaseOrderId = invoice?.linkedPurchaseOrderId
      let linkedPurchaseOrder: IPurchaseOrder | null = null
      if (linkedPurchaseOrderId) {
        linkedPurchaseOrder = await getLinkedPurchaseOrder(
          linkedPurchaseOrderId
        )
      }

      setLineDataBySupplier(
        extractLineDataVariants(items, brands, units, salesTax)
      )

      const receivedDate = invoice?.receivedAt ?? dayjs()
      const ids =
        invoice?.lines
          ?.map((line) => line.linkedItemId)
          .flatMap((id) => (id ? [id] : [])) ?? []

      await getLatestItemCosts(receivedDate, ids)

      setInvoice(
        transformApiInvoiceToUiInvoice(
          invoice ?? new Invoice(),
          linkedPurchaseOrder,
          suppliers
        )
      )

      const gridLines = linkedPurchaseOrder?.lines
        ? combineWithPurchaseOrderLines(
            invoice?.lines ?? [],
            linkedPurchaseOrder.lines,
            salesTax,
            invoice?.unitCostIncludesTax ?? false,
            items ?? {}
          )
        : transformInvoiceLinesToGridInvoiceLines(
            invoice?.lines ?? [],
            salesTax,
            invoice?.unitCostIncludesTax ?? false
          )

      if (
        !linkedPurchaseOrder &&
        !invoice?.isReceived &&
        invoice?.linkedSupplierId &&
        items &&
        units
      ) {
        updateInvoiceLinesWithSupplierVariants(
          gridLines,
          items,
          brands,
          units,
          invoice.linkedSupplierId,
          invoice.unitCostIncludesTax ?? false,
          salesTax
        )
      }

      const normalizedLines = gridLines.reduce<Record<string, UiInvoiceLine>>(
        (acc, line) => {
          acc[line.id] = line
          return acc
        },
        {}
      )

      setLines(normalizedLines)

      return {
        invoice,
        suppliers,
        items,
        brands,
        units,
        salesTax,
        linkedPurchaseOrder,
      }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to initialize invoice data')

      return {
        invoice: null,
        suppliers: null,
        items: null,
        brands: null,
        units: null,
        salesTax: [],
        linkedPurchaseOrder: null,
      }
    }
  },

  createInvoiceFromPurchaseOrder: async (
    purchaseOrderUi: PurchaseOrderUi
  ): Promise<{ invoice: IInvoice; purchaseOrder: IPurchaseOrder } | null> => {
    try {
      const {
        setLinkedPurchaseOrder,
        setLineDataBySupplier,
        setInvoice,
        setLines,
        getStockCost,
      } = get()

      // Get all necessary data from the store
      const { getSuppliers, getItems, getBrands, getStockUnits, getSalesTax } =
        useStockStore.getState()

      const [suppliers, items, brands, units, salesTax] = await Promise.all([
        getSuppliers(),
        getItems(),
        getBrands(),
        getStockUnits(true),
        getSalesTax(),
      ])

      // Create the purchase order as IPurchaseOrder for the linked purchase order
      // Set unitCost to 0 initially - we'll fetch estimated costs separately
      const linkedPurchaseOrder: IPurchaseOrder = {
        id: purchaseOrderUi.id,
        orderNumber: purchaseOrderUi.orderNumber,
        supplierName: purchaseOrderUi.supplierName,
        supplierId: purchaseOrderUi.supplierId,
        createdAt: purchaseOrderUi.createdAt,
        isReceived: purchaseOrderUi.isReceived,
        subtotal: purchaseOrderUi.subtotal,
        creditRequest: purchaseOrderUi.creditRequest,
        linkedInvoiceId: purchaseOrderUi.linkedInvoiceId,
        lines: purchaseOrderUi.lines
          ?.filter((line) => !line.isEmpty && !line.lastLine)
          .map(
            (line) =>
              new PurchaseOrderLine({
                id: line.lineId,
                itemId: line.itemId,
                itemCode: line.itemCode,
                brandId: line.brandId,
                brandName: line.brandName,
                unitId: line.unitId,
                unitName: line.unitName,
                unitRatio: line.unitRatio,
                quantityOrdered: line.quantityOrdered,
                quantityReceived: line.quantityOrdered, //The Quantity Received field defaults to the Quantity Ordered
                unitCost: 0, // Set to 0 initially, will be updated with estimated cost
                taxPercent: line.taxPercent,
              })
          ),
      }

      // Set the linked purchase order
      setLinkedPurchaseOrder(linkedPurchaseOrder)

      // Set line data by supplier
      setLineDataBySupplier(
        extractLineDataVariants(items, brands, units, salesTax)
      )

      // Create the UI invoice (no lines yet, they'll be added by combineWithPurchaseOrderLines)
      // Set issued and due dates to today for manual entry mode
      const today = dayjs()
      const newInvoice = new Invoice({
        supplierName: purchaseOrderUi.supplierName,
        linkedSupplierId: purchaseOrderUi.supplierId,
        linkedPurchaseOrderId: purchaseOrderUi.id,
        purchaseOrderNumber: purchaseOrderUi.orderNumber,
        receivedAt: today,
        issuedAt: today.format(API_DATE_FORMAT),
        dueAt: today.format(API_DATE_FORMAT),
        unitCostIncludesTax: false,
        lines: [],
      })

      const uiInvoice = transformApiInvoiceToUiInvoice(
        newInvoice,
        linkedPurchaseOrder,
        suppliers
      )

      setInvoice(uiInvoice)

      // Create grid lines using the existing logic that combines invoice lines with purchase order lines
      const gridLines = combineWithPurchaseOrderLines(
        [], // Empty invoice lines since we're starting fresh
        linkedPurchaseOrder.lines ?? [],
        salesTax,
        false, // unitCostIncludesTax
        items ?? {},
        true // isFromPurchaseOrder - to show loading spinners
      )

      // Convert to normalized lines dictionary
      const normalizedLines = gridLines.reduce<Record<string, UiInvoiceLine>>(
        (acc, line) => {
          acc[line.id] = line
          return acc
        },
        {}
      )

      setLines(normalizedLines)

      // Fetch estimated costs for all lines asynchronously
      const now = dayjs().toISOString()
      const linesToFetchCosts = Object.values(normalizedLines).filter(
        (line) => line.linkedItemId && line.isPurchaseOrderLine
      )

      // Set awaiting cost state if we have lines to fetch costs for
      if (linesToFetchCosts.length > 0) {
        set(() => ({ awaitingCost: true }))
      }

      const costPromises = linesToFetchCosts.map(async (line) => {
        try {
          const itemIdTimeStrings = [`${line.linkedItemId},${now}`]
          const stockCostData = await getStockCost(itemIdTimeStrings)

          // Get the first StockCost object from all itemCosts
          const firstStockCost = Object.values(stockCostData?.itemCosts ?? {})
            .flat()
            .at(0)

          const estimatedCost = firstStockCost?.cost ?? 0

          // Update the itemCosts state so we can display the price alert arrows
          if (line.linkedItemId && firstStockCost) {
            const itemId = line.linkedItemId
            set((state) => ({
              ...state,
              itemCosts: {
                ...state.itemCosts,
                [itemId]: firstStockCost,
              },
            }))
          }

          // Update the line with the estimated cost
          const { updateInvoiceLine } = get()
          const quantityReceived = line.quantityReceived ?? 0
          const saleTaxRate = line.saleTaxRate ?? 0
          const unitRatio = line.linkedUnitRatio ?? 1

          // Apply unit ratio to convert the estimated cost to the correct unit
          const adjustedUnitCost = roundNumber(estimatedCost * unitRatio, 2)
          const totalCost = roundNumber(adjustedUnitCost * quantityReceived, 2)

          // Calculate tax on the total cost using the helper function
          const taxAmount = calculateTaxOnItem(
            adjustedUnitCost,
            quantityReceived,
            saleTaxRate,
            false // unitCostIncludesTax - assuming false for PO-generated invoices
          )

          updateInvoiceLine(line.id, {
            unitCost: adjustedUnitCost,
            totalCost: totalCost,
            taxAmount: roundNumber(taxAmount, 2),
            isNew: false, // Remove loading state once cost is fetched
          })
        } catch (error) {
          // Still remove loading state for this line on error
          const { updateInvoiceLine } = get()
          updateInvoiceLine(line.id, { isNew: false })
          exceptionMessageHandler(
            error,
            `Failed to fetch cost for item ${line.linkedItemId}:`
          )
        }
      })

      // Execute all cost fetches in parallel and clear awaiting cost state when done
      Promise.allSettled(costPromises).finally(() => {
        set(() => ({ awaitingCost: false }))

        // Calculate and set invoice total using the same logic as the Totals component
        const { invoiceLinesById, invoice, setInvoice } = get()
        const { total } = calculateFooterTotals(
          Object.values(invoiceLinesById),
          invoice?.unitCostIncludesTax ?? false,
          undefined, // No manually entered total yet, we're auto-calculating from lines
          invoice.discountAmount
        )
        setInvoice({ total })
      })

      return {
        invoice: newInvoice,
        purchaseOrder: linkedPurchaseOrder,
      }
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to create invoice from purchase order'
      )
      return null
    }
  },

  setLinkedPurchaseOrder: (
    linkedPurchaseOrder: IPurchaseOrder | undefined
  ): void => {
    set(() => ({
      linkedPurchaseOrder: linkedPurchaseOrder,
    }))
  },

  setLineDataBySupplier: (lineDataBySupplier: LineDataBySupplier): void => {
    set(() => ({
      lineDataBySupplier,
    }))
  },

  setIsGridLoading: (isGridLoading: boolean): void => {
    set(() => ({
      isGridLoading,
    }))
  },

  setInvoice: (updatedInvoice: Partial<UiInvoice>): void => {
    set((state) => ({
      ...state,
      invoice: {
        ...state.invoice,
        ...updatedInvoice,
        //The below is for backward compatibility with AdjustingInvoice.tsx (which uses react-hook-form and operates on the invoice lines in-place in the state)
        lines: updatedInvoice.lines ?? state.invoice?.lines ?? [],
      },
    }))
  },

  setLines: (lines: { [key: string]: UiInvoiceLine }): void => {
    set(() => ({
      invoiceLinesById: lines,
    }))
    get().recalculateInvoiceTotals()
  },

  updateInvoiceLine: (
    lineId: string,
    updatedFields: Partial<UiInvoiceLine>
  ): UiInvoiceLine => {
    set((state) => ({
      ...state,
      invoiceLinesById: {
        ...state.invoiceLinesById,
        [lineId]: {
          ...state.invoiceLinesById[lineId],
          ...updatedFields,
        },
      },
    }))
    get().recalculateInvoiceTotals()
    return get().invoiceLinesById[lineId]
  },

  clearInvoiceLineCode: (invoiceLineId: string) => {
    const { updateInvoiceLine } = get()

    updateInvoiceLine(invoiceLineId, {
      code: undefined,
      itemMatchedOn: undefined,
    })
  },

  updateInvoiceLineCode: (invoiceLineId: string, updatedCode: string) => {
    const { salesTax, invoiceLinesById, invoice, updateInvoiceLine } = get()
    const { items, units, brands } = useStockStore.getState()
    const invoiceLine = invoiceLinesById[invoiceLineId]

    const matchedVariant = findSupplierVariantByCode(
      items,
      updatedCode,
      invoice.linkedSupplierId ?? ''
    )

    if (!matchedVariant || !updatedCode) {
      updateInvoiceLine(invoiceLine.id, {
        code: updatedCode,
        itemMatchedOn: undefined,
      })
      return updatedCode
    }

    let brandId: string | null = null
    let brandName: string | null = null
    let unitId: string | null = null
    let unitName: string | null = null

    if (matchedVariant?.stockItemId) {
      // A supplier can have one or more variants
      const supplierVariants = items[matchedVariant.stockItemId].suppliers

      if (supplierVariants) {
        const defaultSupplierVariant = supplierVariants.find(
          (variant) => variant.defaultForSupplier === true
        )

        if (defaultSupplierVariant) {
          brandId = defaultSupplierVariant.brandId ?? null
          brandName = brandId ? brands[brandId]?.name ?? null : null
          unitId = defaultSupplierVariant.unitId ?? null
          unitName = unitId ? units[unitId].name ?? null : null
        } else {
          // If none of the matched variants are marked as default
          // we will match it with the first one found
          brandId = supplierVariants[0].brandId ?? null
          brandName = brandId ? brands[brandId].name ?? null : null
          unitId = supplierVariants[0].unitId ?? null
          unitName = unitId ? units[unitId].name ?? null : null
        }
      }
    }

    const matchedItem: IItem | undefined = matchedVariant.stockItemId
      ? items[matchedVariant.stockItemId]
      : undefined
    const quantityReceived = invoiceLine.quantityReceived
    const unitCost = invoiceLine.unitCost

    const { saleTaxRate, customTaxRate, selectedSalesTax, taxAmount } =
      calculateTaxRelatedFields(
        matchedItem,
        unitCost,
        quantityReceived,
        salesTax,
        invoice.unitCostIncludesTax ?? false
      )

    const updatedInvoiceLine: Partial<UiInvoiceLine> = {
      ...invoiceLine,
      code: updatedCode,
      linkedItemId: matchedItem?.id,
      linkedBrandId: brandId ?? undefined,
      linkedUnitId: unitId ?? undefined,
      unit: unitName ?? undefined,
      description: matchedItem?.name,
      brand: brandName ?? undefined,
      unitCost: roundNumber(unitCost, 2),
      totalCost: roundNumber(calculateTotalCost(quantityReceived, unitCost), 2),
      quantityReceived: quantityReceived,
      saleTaxRate: saleTaxRate,
      taxAmount: taxAmount,
      selectedSalesTax: selectedSalesTax,
      customTaxRate: customTaxRate,
      itemMatchedOn: MatchedOnType.Code,
    }

    updateInvoiceLine(invoiceLine.id, updatedInvoiceLine)
  },

  mergeLineIfApplicable: (invoiceLine: UiInvoiceLine) => {
    const { invoiceLinesById, updateInvoiceLine, deleteInvoiceLine } = get()

    const purchaseOrderLineMatch = Object.values(invoiceLinesById)
      .filter((line) => line.isPurchaseOrderLine)
      .find(
        (invoiceLineCheck) =>
          (!invoiceLine?.code &&
            invoiceLineCheck.linkedItemId === invoiceLine?.linkedItemId &&
            invoiceLineCheck.linkedUnitId === invoiceLine?.linkedUnitId) ||
          (invoiceLineCheck.code === invoiceLine?.code &&
            invoiceLineCheck.linkedItemId === invoiceLine?.linkedItemId &&
            invoiceLineCheck.linkedUnitId === invoiceLine?.linkedUnitId)
      )

    purchaseOrderLineMatch &&
      updateInvoiceLine(invoiceLine.id, {
        linkedUnitId: purchaseOrderLineMatch.linkedUnitId,
        unit: purchaseOrderLineMatch.unit,
        linkedBrandId: purchaseOrderLineMatch.linkedBrandId,
        brand: purchaseOrderLineMatch.brand,
        isPurchaseOrderLine: true,
        quantityOrdered: purchaseOrderLineMatch.quantityOrdered,
      })

    purchaseOrderLineMatch && deleteInvoiceLine(purchaseOrderLineMatch.id)
  },

  updateInvoiceLineBrand: (invoiceLineId: string, updatedBrand: IBrand) => {
    const { updateInvoiceLine } = get()

    updateInvoiceLine(invoiceLineId, {
      brand: updatedBrand.name,
      linkedBrandId: updatedBrand.id,
    })
  },

  updateInvoiceLineItem: (invoiceLineId: string, updatedItem: IItem) => {
    if (!updatedItem.id) return

    const {
      linkedPurchaseOrder,
      salesTax,
      invoiceLinesById,
      invoice,
      updateInvoiceLine,
      mergeLineIfApplicable,
    } = get()

    const { items, units, brands } = useStockStore.getState()
    const invoiceLine = invoiceLinesById[invoiceLineId]

    const { saleTaxRate, customTaxRate, selectedSalesTax, taxAmount } =
      calculateTaxRelatedFields(
        updatedItem,
        invoiceLine?.unitCost,
        invoiceLine?.quantityReceived,
        salesTax,
        invoice.unitCostIncludesTax ?? false
      )

    const totalCost = roundNumber(
      (invoiceLine?.quantityReceived ?? 0) * (invoiceLine?.unitCost ?? 0),
      2
    )

    const matchedSupplierVariant = getDefaultSupplierVariant(
      invoice.linkedSupplierId ?? '',
      updatedItem.id,
      items
    )

    const linkedUnitId = matchedSupplierVariant?.unitId
      ? matchedSupplierVariant.unitId
      : updatedItem.orderingUnitId

    const updatedInvoiceLine = updateInvoiceLine(invoiceLine.id, {
      taxAmount,
      saleTaxRate,
      totalCost,
      selectedSalesTax,
      customTaxRate,
      description: updatedItem.name,
      linkedItemId: updatedItem.id,
      linkedUnitId: linkedUnitId,
      linkedUnitRatio: units[linkedUnitId ?? '']?.ratio,
      unit: matchedSupplierVariant?.unitId
        ? units[matchedSupplierVariant.unitId]?.name
        : updatedItem.orderingUnitName,
      linkedBrandId: matchedSupplierVariant?.brandId
        ? brands[matchedSupplierVariant.brandId].id
        : updatedItem.defaultBrandId,
      brand: matchedSupplierVariant?.brandId
        ? brands[matchedSupplierVariant.brandId]?.name
        : brands[updatedItem.defaultBrandId ?? '']?.name,
      isPurchaseOrderLine: false,
      quantityOrdered: undefined,
    })

    linkedPurchaseOrder && mergeLineIfApplicable(updatedInvoiceLine)
  },

  updateInvoiceLineUnit: (invoiceLineId: string, updatedUnit: IUnit) => {
    if (!updatedUnit.id) return

    const { linkedPurchaseOrder, updateInvoiceLine, mergeLineIfApplicable } =
      get()

    const updatedInvoiceLine = updateInvoiceLine(invoiceLineId, {
      unit: updatedUnit?.name,
      linkedUnitId: updatedUnit?.id,
      linkedUnitRatio: updatedUnit?.ratio,
    })

    linkedPurchaseOrder && mergeLineIfApplicable(updatedInvoiceLine)
  },

  updateInvoiceLineUnitCost: (
    invoiceLineId: string,
    updatedUnitCost: number
  ) => {
    const { invoiceLinesById, updateInvoiceLine } = get()
    const invoiceLine = invoiceLinesById[invoiceLineId]
    const quantityReceived = invoiceLine.quantityReceived ?? 0
    const saleTaxRate = invoiceLine.saleTaxRate ?? 0

    updateInvoiceLine(invoiceLineId, {
      unitCost: updatedUnitCost,
      taxAmount: roundNumber(
        quantityReceived * updatedUnitCost * saleTaxRate,
        2
      ),
      totalCost: roundNumber(quantityReceived * updatedUnitCost, 2),
    })
  },

  updateInvoiceLineQtdReceived: (
    invoiceLineId: string,
    updatedQuantityReceived: number
  ) => {
    const { invoiceLinesById, updateInvoiceLine } = get()
    const invoiceLine = invoiceLinesById[invoiceLineId]
    const saleTaxRate = invoiceLine.saleTaxRate ?? 0
    const unitCost = invoiceLine?.unitCost ?? 0
    const updatedTotalCost = updatedQuantityReceived * unitCost
    const updatedTaxAmount = updatedQuantityReceived * unitCost * saleTaxRate

    updateInvoiceLine(invoiceLineId, {
      quantityReceived: updatedQuantityReceived,
      totalCost: roundNumber(updatedTotalCost, 2),
      taxAmount: roundNumber(updatedTaxAmount, 2),
    })
  },

  updateInvoiceLineSelectedSalesTax: (
    invoiceLineId: string,
    updatedSelectedSalesTax: number
  ) => {
    const { updateInvoiceLine } = get()

    updateInvoiceLine(invoiceLineId, {
      selectedSalesTax: updatedSelectedSalesTax,
    })
  },

  updateInvoiceLineCustomSalesTax: (
    invoiceLineId: string,
    updatedCustomSalesTax: number
  ) => {
    const { updateInvoiceLine } = get()

    updateInvoiceLine(invoiceLineId, {
      customTaxRate: updatedCustomSalesTax,
    })
  },

  updateInvoiceLineTaxRate: (invoiceLineId: string, updatedTaxRate: number) => {
    const { invoiceLinesById, invoice, updateInvoiceLine } = get()
    const invoiceLine = invoiceLinesById[invoiceLineId]
    const unitCost = invoiceLine?.unitCost ?? 0

    const gstAmount = calculateTaxOnItem(
      unitCost,
      invoiceLine?.quantityReceived ?? 0,
      updatedTaxRate,
      invoice.unitCostIncludesTax ?? false
    )

    updateInvoiceLine(invoiceLineId, {
      taxAmount: roundNumber(gstAmount, 2),
      saleTaxRate: updatedTaxRate,
    })
  },

  updateInvoiceLineDeletedAt: (
    invoiceLineId: string,
    updatedDeletedAt: dayjs.Dayjs | undefined
  ) => {
    const { updateInvoiceLine } = get()

    updateInvoiceLine(invoiceLineId, { deletedAt: updatedDeletedAt })
  },

  updateInvoiceLineTotalCost: (
    invoiceLineId: string,
    updatedTotalCost: number
  ) => {
    const { invoiceLinesById, invoice, updateInvoiceLine } = get()
    const invoiceLine = invoiceLinesById[invoiceLineId]

    // Set quantityReceived to the invoice lines value if it's non-zero
    // otherwise, use 1 or -1 (matching the sign of updatedTotalCost)
    let quantityReceived =
      invoiceLine?.quantityReceived && invoiceLine.quantityReceived !== 0
        ? invoiceLine.quantityReceived
        : roundNumber(updatedTotalCost >= 0 ? 1 : -1, 3)

    // If quantityReceived's sign (negative or postive) does not match the updatedTotal's sign
    // update quantityRecieved to match the sign.
    // We want unitCost to always remain positive thus we change the quantityReceived.
    if (
      Math.sign(updatedTotalCost) != 0 &&
      Math.sign(quantityReceived!) !== Math.sign(updatedTotalCost)
    ) {
      quantityReceived = roundNumber(quantityReceived! * -1, 3)
    }

    // It's a bit counter-intuitive, but the Total field is ALWAYS
    // quantity recieved * unit cost (ignoring tax).
    // This was defined in LOADED-4495 and then modified in LOADED-5719
    const unitCost = updatedTotalCost / quantityReceived!
    const taxAmount = calculateTaxOnItem(
      unitCost,
      quantityReceived!,
      invoiceLine?.saleTaxRate ?? 0,
      invoice.unitCostIncludesTax ?? false
    )

    updateInvoiceLine(invoiceLine?.id, {
      quantityReceived: quantityReceived,
      totalCost: updatedTotalCost,
      unitCost: roundNumber(unitCost, 2),
      taxAmount: roundNumber(taxAmount, 2),
    })
  },

  updateInvoiceUnitCostIncludesTax: (updateUnitCostIncludesTax: boolean) => {
    const {
      invoiceLinesById,
      setInvoice,
      setLines,
      updateDiscountPercentage,
      recalculateInvoiceTotals,
    } = get()

    setInvoice({ unitCostIncludesTax: updateUnitCostIncludesTax })

    const updatedLines = {}
    for (const lineId in invoiceLinesById) {
      const line = invoiceLinesById[lineId]
      updatedLines[lineId] = {
        ...line,
        taxAmount: calculateTaxOnItem(
          line.unitCost ?? 0,
          line.quantityReceived ?? 0,
          line.saleTaxRate ?? 0,
          updateUnitCostIncludesTax
        ),
      }
    }
    setLines(updatedLines)

    updateDiscountPercentage()
    recalculateInvoiceTotals()
  },

  updateInvoiceDiscount: (discountAmount: number | undefined = 0) => {
    const { setInvoice, updateDiscountPercentage, recalculateInvoiceTotals } =
      get()

    setInvoice({ discountAmount })

    updateDiscountPercentage()
    recalculateInvoiceTotals()
  },

  updateDiscountPercentage: () => {
    const { setLines, invoice, invoiceLinesById } = get()

    const totals = calculateFooterTotals(
      dictionaryToArray(invoiceLinesById),
      invoice.unitCostIncludesTax ?? false,
      invoice.total,
      invoice.discountAmount
    )

    //Don't count the discount itself (or any rounding cents) here, hence don't use the grand total
    const invoiceTotal = (totals.subtotal ?? 0) + (totals.taxTotal ?? 0)
    const discountPercent = (invoice.discountAmount ?? 0) / invoiceTotal

    const updatedLines = {}
    for (const lineId in invoiceLinesById) {
      const line = invoiceLinesById[lineId]
      updatedLines[lineId] = {
        ...line,
        discountPercent,
      }
    }
    setLines(updatedLines)
  },

  mergePurchaseOrderLinesWithInvoiceLines: (
    purchaseOrder: IPurchaseOrder | undefined,
    isFromPurchaseOrder: boolean = false
  ) => {
    const {
      invoiceLinesById,
      salesTax,
      invoice,
      setInvoice,
      setLinkedPurchaseOrder,
      setLines,
      getLatestItemCosts,
      updateInvoiceLine,
      setAwaitingCost,
    } = get()
    const { items, brands, units } = useStockStore.getState()

    // If the purchase order is not null then we set the supplier name and supplier id
    // from the Purchase Order to the invoice. It is not possible to have no supplier id/name
    // in a purchase order
    purchaseOrder && setInvoice({ supplierName: purchaseOrder.supplierName })

    setInvoice({
      purchaseOrderNumber: purchaseOrder?.orderNumber ?? undefined,
      linkedPurchaseOrderId: purchaseOrder?.id ?? undefined,
    })

    setLinkedPurchaseOrder(purchaseOrder)

    const invoiceLines = dictionaryToArray(invoiceLinesById)

    const updatedLines = purchaseOrder
      ? combineWithPurchaseOrderLines(
          invoiceLines,
          purchaseOrder.lines ?? [],
          salesTax,
          invoice.unitCostIncludesTax ?? false,
          items,
          isFromPurchaseOrder
        )
      : removePurchaseOrderLines(invoiceLines)

    !purchaseOrder &&
      updateInvoiceLinesWithSupplierVariants(
        updatedLines,
        items,
        brands,
        units,
        invoice?.linkedSupplierId ?? '',
        invoice?.unitCostIncludesTax ?? false,
        salesTax
      )

    const normalizedLines = updatedLines.reduce<Record<string, UiInvoiceLine>>(
      (acc, line) => {
        acc[line.id] = line
        return acc
      },
      {}
    )

    setLines(normalizedLines)

    // Get latest costs for unmerged purchase order lines only
    if (purchaseOrder) {
      const unmergedPoLines = updatedLines.filter(
        (line) =>
          line.isInvoiceLine === false && line.isPurchaseOrderLine === true
      )

      if (unmergedPoLines.length > 0) {
        // Only fetch costs for lines that need them (unmerged PO lines)
        const linesToFetchCosts = unmergedPoLines.reduce<
          Record<string, UiInvoiceLine>
        >((acc, line) => {
          acc[line.id] = line
          return acc
        }, {})

        fetchAndUpdateLineCosts(
          linesToFetchCosts,
          dayjs(),
          getLatestItemCosts,
          updateInvoiceLine,
          setAwaitingCost
        )
      }
    }
  },

  // Updates the item, brand, and unit links for each line based on a given supplier
  updateInvoiceLineLinks: (supplierId: string | undefined) => {
    const {
      invoiceLinesById,
      salesTax,
      invoice,
      lineDataBySupplier,
      updateInvoiceLine,
      updateLineWithVariant,
      updateLineWithItem,
      setIsGridLoading,
    } = get()
    const { items, brands, units } = useStockStore.getState()
    const invoiceLines = dictionaryToArray(invoiceLinesById)

    setIsGridLoading(true)

    const lineDataVariants: LineDataVariant[] =
      lineDataBySupplier && supplierId
        ? lineDataBySupplier[supplierId] ?? []
        : []

    invoiceLines.forEach((line) => {
      // We currently choose the first matched item variant based on the stock code. In
      // future we plan on introducing a 'default variant' flag to a suppliers items so that
      // the customer is able to influence which variant we use here.
      // It is possible for suppliers to have 0 variants, which will have lineDataVariants == undefined.
      if (line.code && lineDataVariants.length > 0) {
        const variant = lineDataVariants.find(
          (v) =>
            v.stockCode?.toLocaleLowerCase() === line.code?.toLocaleLowerCase()
        )
        // If there is a item supplier variant with the line's stock code then override all relevant fields
        // and continue to the next line
        if (variant) {
          updateLineWithVariant(line, variant)
          return
        }
      }

      // If no stock code or variant found, check if the line description matches the name
      // of an item in Loaded exactly
      const item = findMatching(items, 'name', line.description)
      // If found item then override all relevant fields
      if (item) {
        updateLineWithItem(line, item)
      } else {
        const { saleTaxRate, customTaxRate, selectedSalesTax, taxAmount } =
          calculateTaxRelatedFields(
            undefined,
            line.unitCost,
            line.quantityReceived,
            salesTax,
            invoice.unitCostIncludesTax ?? false
          )
        updateInvoiceLine(line.id, {
          saleTaxRate,
          customTaxRate,
          selectedSalesTax,
          taxAmount,
        })
      }

      // Check and see if the line's brand matches a brand in Loaded exactly.
      // If found set the line's brand as that.
      const brand = findMatching(brands, 'name', line.brand)
      // If a the line's brand matches a brand in Loaded then override the brand fields
      if (brand) {
        updateInvoiceLine(line.id, {
          brand: brand.name ?? undefined,
          linkedBrandId: brand.id,
        })
      }

      // Check and see if the line's unit matches a unit in Loaded exactly.
      // If found set the line's unit as that.
      const unit = findMatching(units, 'name', line.unit)
      // If a the line's unit matches a unit in Loaded then override the unit fields
      if (unit) {
        updateInvoiceLine(line.id, {
          unit: unit.name ?? undefined,
          linkedUnitId: unit.id,
        })
      }
    })

    setIsGridLoading(false)
  },

  // Updates the line with the found variant
  updateLineWithVariant: (line: UiInvoiceLine, variant: LineDataVariant) => {
    const { invoice, updateInvoiceLine } = get()

    line.linkedItemId = variant.stockItemId
    line.linkedBrandId = variant.brandId
    line.linkedUnitId = variant.unitId
    line.description = variant.itemName
    line.brand = variant.brandName
    line.unit = variant.unitName
    line.saleTaxRate = variant.saleTaxRate
    line.itemMatchedOn = MatchedOnType.Code
    line.selectedSalesTax = variant.selectedSalesTax
    line.customTaxRate = (variant.saleTaxRate ?? 0) * 100

    if (line.unitCost && line.quantityReceived && variant.saleTaxRate) {
      line.taxAmount = calculateTaxOnItem(
        line.unitCost,
        line.quantityReceived,
        variant.saleTaxRate,
        invoice.unitCostIncludesTax ?? false
      )
    }

    updateInvoiceLine(line.id, line)
  },

  updateInvoiceSupplier: (
    supplierName: string | undefined,
    supplierId?: string
  ) => {
    const {
      invoice,
      setInvoice,
      queryForMatchingInvoice,
      updateInvoiceLineLinks,
      setLineDataBySupplier,
      salesTax,
    } = get()
    const { suppliers, items, brands, units } = useStockStore.getState()

    const invoiceNumber = invoice.referenceNumber
    // This sends a request to the backend to check if there is an already received invoice
    // with the same invoice number and the same supplier name.
    if (invoiceNumber && supplierName) {
      queryForMatchingInvoice(invoiceNumber, supplierName)
    }

    const systemSupplierId =
      supplierId ?? findMatching(suppliers, 'name', supplierName)?.id

    setInvoice({
      supplierName: supplierName,
      linkedSupplierId: systemSupplierId,
    })

    // Refresh lineDataBySupplier for the new supplier
    setLineDataBySupplier(
      extractLineDataVariants(items, brands, units, salesTax)
    )

    updateInvoiceLineLinks(systemSupplierId)
  },

  // Updates the line with the found item
  updateLineWithItem: (line: UiInvoiceLine, item: IItem) => {
    const { invoice, salesTax, updateInvoiceLine } = get()

    line.linkedItemId = item.id
    line.description = item.name
    line.itemMatchedOn = MatchedOnType.Description

    const { saleTaxRate, customTaxRate, selectedSalesTax, taxAmount } =
      calculateTaxRelatedFields(
        item,
        line.unitCost,
        line.quantityReceived,
        salesTax,
        invoice.unitCostIncludesTax ?? false
      )

    line.saleTaxRate = saleTaxRate
    line.customTaxRate = customTaxRate
    line.selectedSalesTax = selectedSalesTax
    line.taxAmount = taxAmount

    updateInvoiceLine(line.id, line)
  },

  addInvoiceLine: (lineId: string, newLine: UiInvoiceLine) => {
    set((state) => ({
      ...state,
      invoiceLinesById: {
        ...state.invoiceLinesById,
        [lineId]: newLine,
      },
    }))
    get().recalculateInvoiceTotals()
  },

  updateInvoiceReceivedAt: (updatedReceivedAt: dayjs.Dayjs | undefined) => {
    const { setInvoice } = get()

    setInvoice({ receivedAt: updatedReceivedAt })
  },

  updateInvoiceReferenceNumber: (
    updatedReferenceNumber: string | undefined
  ) => {
    const { setInvoice } = get()

    setInvoice({ referenceNumber: updatedReferenceNumber })
  },

  updateInvoiceIssuedAt: (updatedIssuedAt: string | undefined) => {
    const { setInvoice } = get()

    setInvoice({ issuedAt: updatedIssuedAt })
  },

  updateInvoiceDueAt: (updatedDueAt: string | undefined) => {
    const { setInvoice } = get()

    setInvoice({ dueAt: updatedDueAt })
  },

  updateInvoiceTotal: (updatedTotal: number | undefined) => {
    const { setInvoice } = get()

    setInvoice({ total: updatedTotal })
  },

  deleteInvoiceLine: (lineId: string) => {
    set((state) => {
      if (!state.invoiceLinesById[lineId]) {
        return state // Return current state unmodified
      }

      const { [lineId]: _, ...remainingLines } = state.invoiceLinesById
      return {
        ...state,
        invoiceLinesById: remainingLines,
      }
    })
    get().recalculateInvoiceTotals()
  },

  setIsReceiveGoodsButtonEnabled: (
    isReceiveGoodsButtonEnabled: boolean
  ): void => {
    set(() => ({
      isReceiveGoodsButtonEnabled,
    }))
  },

  setShowDiscountedItemCosts: (showDiscountedItemCosts: boolean): void => {
    set(() => ({
      showDiscountedItemCosts,
    }))
  },

  setAwaitingCost: (awaitingCost: boolean): void => {
    set(() => ({
      awaitingCost,
    }))
  },

  queryForMatchingInvoice: (
    invoiceNumber: string,
    supplierName: string
  ): Promise<IInvoice[] | null> => {
    const query: InvoiceQueryConditions = {
      start: dayjs('0001-01-01'),
      end: dayjs('9999-12-31'),
      invoiceNumber: invoiceNumber.trim(),
      supplierName: supplierName.trim(),
      exactMatch: true,
      // As this request is querying to see if there is an exact match of
      // another invoice with the same supplier and invoice number
      // we just default the pagination/sorting to this, as there
      // should only be at most a few invoices that match.
      // Feel free to changed if needed.
      page: 0,
      pageSize: 20,
      sort: 'issuedAt desc',
    }
    return invoiceService
      .getInvoices(query)
      .then((response) => {
        const result = response.result
        set(() => ({
          // Array is sorted by issuedAt date in descending order.
          // This is because if there is multiple we want the most recent
          // invoice to be the first one in the list for easy access.
          matchingInvoices: result
            .sort(
              (a, b) =>
                dayjs(b.issuedAt).valueOf() - dayjs(a.issuedAt).valueOf()
            )
            // Also filter out any invoices that have not been received.
            // InvoiceQueryConditions has a 'isReceived' field but that
            // is only used if the query does not searchign for a supplierName
            // or invoiceNumber
            .filter((i) => i.isReceived),
        }))
        return result
      })
      .catch((error) => {
        set(() => ({
          matchingInvoices: [],
        }))
        exceptionMessageHandler(
          error,
          'Failed to query for duplicated invoices'
        )
        return null
      })
  },

  getStockCost: (
    itemIdTimeStrings?: string[],
    recipeIdTimeStrings?: string[],
    variantIdTimeStrings?: string[],
    stocktakeId?: string
  ): Promise<StockCostResponse | null> => {
    return stockCostService
      .getStockCost(
        itemIdTimeStrings,
        recipeIdTimeStrings,
        variantIdTimeStrings,
        stocktakeId
      )
      .then((response) => response)
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to fetch stock cost(s)')
        return null
      })
  },

  populatePriceAlertRecentStockHistory: async (
    itemId: string,
    invoiceReceivedAt: Dayjs
  ): Promise<IStockReceived[] | undefined> => {
    try {
      const priceAlertRecentStockHistory =
        await stockReceivedService.getStockReceivedItemsByReferenceDate(
          invoiceReceivedAt,
          undefined,
          3, //How many rows we want to return
          undefined,
          itemId
        )

      set((state) => ({
        priceAlertRecentStockCostHistory: {
          ...state.priceAlertRecentStockCostHistory,
          [itemId]: priceAlertRecentStockHistory,
        },
      }))

      return priceAlertRecentStockHistory
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to retrieve price alert recent stock history'
      )
    }
  },

  populatePriceAlertDishRecipes: async (
    itemId: string,
    invoiceId: string
  ): Promise<ICostDifference[]> => {
    try {
      const priceAlertDishRecipes =
        await costDifferenceService.getCostDifference(itemId, invoiceId)

      set((state) => ({
        priceAlertDishRecipes: {
          ...state.priceAlertDishRecipes,
          [itemId]: priceAlertDishRecipes,
        },
      }))

      return priceAlertDishRecipes
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to retrieve price alert dish recipes'
      )
      return []
    }
  },

  searchInvoices: async (
    searchTerm: string,
    limit?: number,
    unreceivedWithoutPo?: boolean
  ): Promise<InvoiceSearchResult[] | null> => {
    set(() => ({ isLoadingInvoiceSearch: true }))
    try {
      const results = await invoiceService.searchInvoices(
        searchTerm,
        limit,
        unreceivedWithoutPo
      )
      // Filter out the current invoice from results if it exists
      const currentInvoiceId = get().invoice?.id
      const filteredResults =
        results && currentInvoiceId
          ? results.filter((result) => result.id !== currentInvoiceId)
          : results || []

      set(() => ({ invoiceSearchResults: filteredResults }))
      return results
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to search invoices')
      set(() => ({ invoiceSearchResults: [] }))
      return null
    } finally {
      set(() => ({ isLoadingInvoiceSearch: false }))
    }
  },

  linkInvoiceToPurchaseOrder: async (
    invoiceId?: string
  ): Promise<UiInvoice | null> => {
    if (!invoiceId) {
      throw new Error(
        'No invoiceId passed when trying to link Invoice to a PurchaseOrder'
      )
    }

    set(() => ({ isLoading: true }))

    try {
      const {
        loadInvoice,
        getInitialInvoice,
        linkedPurchaseOrder,
        salesTax,
        invoice,
        setLines,
      } = get()
      const { suppliers } = useStockStore.getState()
      const { setInvoice, mergePurchaseOrderLinesWithInvoiceLines } = get()

      // Load the selected invoice from the DB
      const selectedInvoice = await loadInvoice(invoiceId)

      if (!selectedInvoice) {
        throw new Error('Failed to load selected invoice')
      }

      await getInitialInvoice(invoiceId)

      const transformedInvoice = transformApiInvoiceToUiInvoice(
        selectedInvoice,
        linkedPurchaseOrder,
        suppliers
      )

      // Preserve the linkedPurchaseOrderId from current state since selectedInvoice doesn't have it
      const linkedInvoice = {
        ...transformedInvoice,
        linkedPurchaseOrderId: invoice.linkedPurchaseOrderId,
        purchaseOrderNumber: invoice.purchaseOrderNumber,
      }

      setInvoice(linkedInvoice)

      // First, populate state with the selected invoice lines
      if (selectedInvoice.lines) {
        const invoiceGridLines = transformInvoiceLinesToGridInvoiceLines(
          selectedInvoice.lines,
          salesTax,
          invoice.unitCostIncludesTax ?? false
        )

        const normalizedInvoiceLines = invoiceGridLines.reduce<
          Record<string, UiInvoiceLine>
        >((acc, line) => {
          acc[line.id] = line
          return acc
        }, {})

        setLines(normalizedInvoiceLines)
      }

      // Then merge in the purchase order lines
      // linkInvoiceToPurchaseOrder is only called from "Receive Goods" flow, so show loading state
      linkedPurchaseOrder &&
        mergePurchaseOrderLinesWithInvoiceLines(linkedPurchaseOrder, true)

      return linkedInvoice
    } catch (error) {
      exceptionMessageHandler(
        error,
        'Failed to link an invoice to a purchase order'
      )
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  unlinkInvoiceFromPurchaseOrder: (): void => {
    const {
      linkedPurchaseOrder,
      salesTax,
      invoice,
      setLines,
      setInvoice,
      updateInvoiceLine,
      setAwaitingCost,
      getLatestItemCosts,
    } = get()
    const { items } = useStockStore.getState()

    const today = dayjs()

    // Clear invoice-specific fields
    const updatedInvoice: Partial<UiInvoice> = {
      id: undefined,
      referenceNumber: undefined,
      issuedAt: today.format(API_DATE_FORMAT),
      dueAt: today.format(API_DATE_FORMAT),
      total: undefined,
      version: undefined,
      subtotal: undefined,
      discountAmount: undefined,
      taxAmount: undefined,
      taxNumber: undefined,
      supplierAddress: undefined,
      billingName: undefined,
      billingAddress: undefined,
      shippingName: undefined,
      shippingAddress: undefined,
      paymentTerms: undefined,
      isReceived: undefined,
      fileId: undefined,
      type: undefined,
      statementId: undefined,
      createdBy: undefined,
      createdAt: undefined,
      reconciled: undefined,
    }

    setInvoice(updatedInvoice)

    // Reset lines to purchase order lines only
    if (linkedPurchaseOrder && items) {
      const purchaseOrderLines = combineWithPurchaseOrderLines(
        [], // Empty invoice lines
        linkedPurchaseOrder.lines || [],
        salesTax,
        invoice.unitCostIncludesTax ?? false,
        items,
        true // isFromPurchaseOrder - to show loading spinners initially
      )

      // Convert to normalized lines dictionary
      const normalizedLines = purchaseOrderLines.reduce<
        Record<string, UiInvoiceLine>
      >((acc, line) => {
        acc[line.id] = line
        return acc
      }, {})

      setLines(normalizedLines)

      // Fetch estimated costs for all lines asynchronously using the helper
      fetchAndUpdateLineCosts(
        normalizedLines,
        dayjs(),
        getLatestItemCosts,
        updateInvoiceLine,
        setAwaitingCost
      )
    }
  },

  linkProcessedInvoice: async (): Promise<void> => {
    const { processedInvoice, linkInvoiceToPurchaseOrder } = get()

    if (!processedInvoice?.id) {
      exceptionMessageHandler(
        new Error('No processed invoice to link'),
        'No processed invoice found'
      )
      return
    }

    try {
      // Link the uploaded invoice to the purchase order
      const linkedInvoice = await linkInvoiceToPurchaseOrder(
        processedInvoice.id
      )

      if (linkedInvoice) {
        enqueueLoadedSnackbar(
          'Invoice uploaded and linked to Purchase Order',
          'success'
        )

        // Clear processing state
        get().clearProcessingState()
      }
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to link invoice to purchase order')
    }
  },

  clearProcessingState: (): void => {
    set(() => ({
      processingInvoiceId: null,
      processedInvoice: null,
    }))
  },
})

export default createInvoiceSlice
